import { useState, useRef, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout.jsx';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, Camera, FileText, X, CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { uploadHealthRecord, getFamilyMembers } from '@/services/api';
import { toast } from 'sonner';

const recordTypes = [
  { value: 'blood_test', label: 'Blood Test (CBC, Lipid Profile, etc.)' },
  { value: 'imaging', label: 'Imaging (X-Ray, MRI, CT Scan)' },
  { value: 'checkup', label: 'General Health Checkup' },
  { value: 'diabetes', label: 'Diabetes Panel' },
  { value: 'thyroid', label: 'Thyroid Function Test' },
  { value: 'liver', label: 'Liver Function Test' },
  { value: 'kidney', label: 'Kidney Function Test' },
  { value: 'cardiac', label: 'Cardiac Tests (ECG, Echo)' },
  { value: 'other', label: 'Other' }
];

function UploadPage() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [recordType, setRecordType] = useState('');
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [testDate, setTestDate] = useState('');
  const [memberId, setMemberId] = useState('');
  const [familyMembers, setFamilyMembers] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  useEffect(function() {
    loadFamilyMembers();
  }, []);

  async function loadFamilyMembers() {
    try {
      const members = await getFamilyMembers();
      setFamilyMembers(members);
    } catch (error) {
      console.error('Error loading family members:', error);
    }
  }

  function handleFileChange(selectedFile) {
    if (selectedFile) {
      const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (validTypes.indexOf(selectedFile.type) === -1) {
        toast.error('Please upload a PDF or image file');
        return;
      }
      
      setFile(selectedFile);
      setUploadResult(null);
      
      if (selectedFile.type.indexOf('image/') === 0) {
        const reader = new FileReader();
        reader.onload = function(e) { setPreview(e.target.result); };
        reader.readAsDataURL(selectedFile);
      } else {
        setPreview(null);
      }
    }
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    handleFileChange(droppedFile);
  }

  function handleDragOver(e) {
    e.preventDefault();
    setDragOver(true);
  }

  function handleDragLeave(e) {
    e.preventDefault();
    setDragOver(false);
  }

  async function handleUpload() {
    if (!file) {
      toast.error('Please select a file');
      return;
    }
    if (!recordType) {
      toast.error('Please select a record type');
      return;
    }
    if (!title) {
      toast.error('Please enter a title');
      return;
    }

    setUploading(true);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('record_type', recordType);
      formData.append('title', title);
      if (notes) formData.append('notes', notes);
      if (testDate) formData.append('test_date', testDate);
      if (memberId && memberId !== 'self') formData.append('member_id', memberId);

      const result = await uploadHealthRecord(formData);
      setUploadResult(result);
      toast.success('Record uploaded and analyzed successfully!');
      
      setFile(null);
      setPreview(null);
      setRecordType('');
      setTitle('');
      setNotes('');
      setTestDate('');
      setMemberId('');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload record');
    } finally {
      setUploading(false);
    }
  }

  function clearFile() {
    setFile(null);
    setPreview(null);
    setUploadResult(null);
  }

  function getAnalysisMetrics() {
    if (!uploadResult) return [];
    const analysis = uploadResult.analysis;
    if (!analysis) return [];
    return analysis.metrics || [];
  }

  function getAnalysisFindings() {
    if (!uploadResult) return [];
    const analysis = uploadResult.analysis;
    if (!analysis) return [];
    return analysis.abnormal_findings || [];
  }

  function getAnalysisInsights() {
    if (!uploadResult) return [];
    const analysis = uploadResult.analysis;
    if (!analysis) return [];
    return analysis.insights || [];
  }

  function getAnalysisRecommendations() {
    if (!uploadResult) return [];
    const analysis = uploadResult.analysis;
    if (!analysis) return [];
    return analysis.recommendations || [];
  }

  function getReportType() {
    if (!uploadResult) return '';
    const analysis = uploadResult.analysis;
    if (!analysis) return '';
    return analysis.report_type || '';
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="upload-page">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Upload Health Record
          </h1>
          <p className="text-slate-600 mt-1">
            Upload your medical reports and our AI will analyze them automatically
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Document Upload
              </CardTitle>
              <CardDescription>
                Upload a PDF or take a photo of your report
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {!file && (
                <div
                  className={dragOver ? "upload-zone drag-over rounded-xl p-8 text-center cursor-pointer" : "upload-zone rounded-xl p-8 text-center cursor-pointer"}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onClick={function() { fileInputRef.current.click(); }}
                  data-testid="upload-dropzone"
                >
                  <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <p className="text-slate-600 font-medium mb-2">
                    Drag and drop your file here
                  </p>
                  <p className="text-sm text-slate-400 mb-4">
                    or click to browse (PDF, JPG, PNG)
                  </p>
                  <div className="flex justify-center gap-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={function(e) {
                        e.stopPropagation();
                        fileInputRef.current.click();
                      }}
                      data-testid="browse-files-btn"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Browse Files
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={function(e) {
                        e.stopPropagation();
                        cameraInputRef.current.click();
                      }}
                      className="camera-btn text-white border-0"
                      data-testid="take-photo-btn"
                    >
                      <Camera className="w-4 h-4 mr-2" />
                      Take Photo
                    </Button>
                  </div>
                </div>
              )}

              {file && (
                <div className="border border-slate-200 rounded-xl p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      {preview ? (
                        <img 
                          src={preview} 
                          alt="Preview" 
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-red-100 rounded-lg flex items-center justify-center">
                          <FileText className="w-8 h-8 text-red-600" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900 truncate">{file.name}</p>
                        <p className="text-sm text-slate-500">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={clearFile}
                      className="text-slate-400 hover:text-red-600"
                      data-testid="clear-file-btn"
                    >
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={function(e) { handleFileChange(e.target.files[0]); }}
                className="hidden"
                data-testid="file-input"
              />
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={function(e) { handleFileChange(e.target.files[0]); }}
                className="hidden"
                data-testid="camera-input"
              />
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Record Details
              </CardTitle>
              <CardDescription>
                Provide information about the report
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="recordType">Record Type *</Label>
                <Select value={recordType} onValueChange={setRecordType}>
                  <SelectTrigger id="recordType" data-testid="record-type-select">
                    <SelectValue placeholder="Select record type" />
                  </SelectTrigger>
                  <SelectContent>
                    {recordTypes.map(function(type) {
                      return (
                        <SelectItem key={type.value} value={type.value} data-testid={`record-type-option-${type.value}`}>
                          {type.label}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  placeholder="e.g., Annual Blood Test Results"
                  value={title}
                  onChange={function(e) { setTitle(e.target.value); }}
                  data-testid="title-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="testDate">Test Date</Label>
                <Input
                  id="testDate"
                  type="date"
                  value={testDate}
                  onChange={function(e) { setTestDate(e.target.value); }}
                  data-testid="test-date-input"
                />
              </div>

              {familyMembers.length > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="member">For Family Member</Label>
                  <Select value={memberId} onValueChange={setMemberId}>
                    <SelectTrigger id="member" data-testid="member-select">
                      <SelectValue placeholder="Select member (optional)" />
                    </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="self" data-testid="member-option-self">Myself</SelectItem>
                    {familyMembers.map(function(member) {
                      return (
                        <SelectItem
                          key={member.user_id}
                          value={member.user_id}
                          data-testid={`member-option-${member.user_id}`}
                        >
                          {member.full_name} ({member.relationship})
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  placeholder="Any additional notes about this report..."
                  value={notes}
                  onChange={function(e) { setNotes(e.target.value); }}
                  rows={3}
                  data-testid="notes-textarea"
                />
              </div>

              <Button
                className="w-full bg-blue-900 hover:bg-blue-800 text-white rounded-lg py-5 font-medium btn-active"
                onClick={handleUpload}
                disabled={!file || !recordType || !title || uploading}
                data-testid="upload-submit-btn"
              >
                {uploading ? (
                  <span className="flex items-center justify-center">
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Analyzing Document...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <Upload className="w-5 h-5 mr-2" />
                    Upload & Analyze
                  </span>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        {uploadResult && (
          <Card className="bg-white border-slate-200 shadow-sm" data-testid="analysis-results">
            <CardHeader className="bg-green-50 border-b border-green-100 rounded-t-xl">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-green-600" />
                <div>
                  <CardTitle className="text-lg text-green-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Analysis Complete
                  </CardTitle>
                  <CardDescription className="text-green-600">
                    AI has extracted and analyzed your health data
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {getReportType() && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2">Report Type</h3>
                  <p className="text-slate-900 font-medium">{getReportType()}</p>
                </div>
              )}

              {getAnalysisMetrics().length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Extracted Metrics</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {getAnalysisMetrics().slice(0, 9).map(function(metric, index) {
                      const isHigh = metric.status === 'high' || metric.status === 'concerning';
                      const isLow = metric.status === 'low';
                      return (
                        <div 
                          key={index}
                          className={isHigh ? "p-4 rounded-xl border bg-red-50 border-red-200" : isLow ? "p-4 rounded-xl border bg-amber-50 border-amber-200" : "p-4 rounded-xl border bg-slate-50 border-slate-200"}
                        >
                          <p className="text-sm text-slate-600">{metric.name}</p>
                          <p className="text-xl font-bold text-slate-900 mt-1">
                            {metric.value} {metric.unit}
                          </p>
                          {metric.normal_range && (
                            <p className="text-xs text-slate-500 mt-1">Normal: {metric.normal_range}</p>
                          )}
                          <span className={isHigh ? "inline-block text-xs font-medium px-2 py-0.5 rounded-full mt-2 status-high" : isLow ? "inline-block text-xs font-medium px-2 py-0.5 rounded-full mt-2 status-low" : "inline-block text-xs font-medium px-2 py-0.5 rounded-full mt-2 status-normal"}>
                            {metric.status ? metric.status.toUpperCase() : 'NORMAL'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {getAnalysisFindings().length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Abnormal Findings</h3>
                  <div className="space-y-2">
                    {getAnalysisFindings().map(function(finding, index) {
                      return (
                        <div key={index} className="flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-lg">
                          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                          <p className="text-slate-800">{finding}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {getAnalysisInsights().length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">AI Insights</h3>
                  <div className="space-y-2">
                    {getAnalysisInsights().map(function(insight, index) {
                      return (
                        <div key={index} className="insight-card p-4 bg-sky-50 rounded-lg">
                          <p className="text-slate-700">{insight}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {getAnalysisRecommendations().length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Recommendations</h3>
                  <ul className="space-y-2">
                    {getAnalysisRecommendations().map(function(rec, index) {
                      return (
                        <li key={index} className="flex items-start gap-2 text-slate-700">
                          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span>{rec}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}

export default UploadPage;
